import React, { useMemo, useRef, useState } from 'react';
import { Link, Navigate, Routes, Route, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { LdbProvider, useLdb } from '../contexts/LdbContext';
import { LDB_SECTIONS, LDB_BASE, ldbT } from '../lib/ldb/i18n';
import { Pill, fmtUsd, progressPct, downloadCsv, isOverdue } from '../lib/ldb/utils';
import '../styles/ldb.css';

function LdbModal({ title, fields, initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || {});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="ldb-modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ldb-modal ldb-sans bg-white w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-5">{title}</h3>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {fields.map((f) => (
            <label key={f.key} className="block text-xs font-semibold uppercase text-gray-500">
              {f.label}
              {f.type === 'select' ? (
                <select className="ldb-input ldb-sans mt-1 w-full px-2 py-1.5 text-sm block" value={form[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)}>
                  {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea className="ldb-input ldb-sans mt-1 w-full px-2 py-1.5 text-sm block" rows={3} value={form[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} />
              ) : (
                <input type={f.type || 'text'} className="ldb-input ldb-sans mt-1 w-full px-2 py-1.5 text-sm block" value={form[f.key] ?? ''} onChange={(e) => set(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)} />
              )}
            </label>
          ))}
        </div>
        <div className="flex gap-2 mt-6 justify-end">
          <button type="button" className="ldb-btn-outline-dark ldb-sans px-4 py-2 text-sm" onClick={onClose}>Cancel</button>
          <button type="button" className="ldb-btn-primary ldb-sans px-4 py-2 text-sm" onClick={() => onSave(form)}>Save</button>
        </div>
      </div>
    </div>
  );
}

function DashboardView() {
  const { state, metrics, lang } = useLdb();
  const t = (k) => ldbT(lang, k);
  const { settings } = state;
  const upcoming = [...state.grants]
    .filter((g) => g.status !== 'awarded' && g.deadline)
    .sort((a, b) => a.deadline.localeCompare(b.deadline))
    .slice(0, 5);

  const attention = [];
  state.grants.filter((g) => g.compliance === 'board').forEach((g) => {
    attention.push({ text: `Charter §3: ${g.program} — $${g.amount.toLocaleString()}`, tag: 'BOARD APPROVAL + CHAIR CO-SIGN REQUIRED' });
  });
  state.followups.filter((f) => f.status === 'overdue' || isOverdue(f.dueDate)).forEach((f) => {
    attention.push({ text: `Overdue follow-up: ${f.organization} ${f.dueDate}`, tag: 'OVERDUE' });
  });

  const kpis = [
    { label: t('dashboard.fundsRaised'), value: fmtUsd(metrics.fundsRaised), sub: `${t('dashboard.target')} ${fmtUsd(settings.raiseFloor)}–${fmtUsd(settings.raiseStretch)}`, pct: progressPct(metrics.fundsRaised, settings.raiseFloor) },
    { label: t('dashboard.payingSubscribers'), value: `${metrics.activeSubs} / ${settings.payingSubscribersTarget}`, sub: t('dashboard.haohaoxuexi'), pct: progressPct(metrics.activeSubs, settings.payingSubscribersTarget) },
    { label: t('dashboard.signedMous'), value: `${metrics.signedMous} / ${settings.tier1MousTarget}`, sub: `${metrics.inDiscussion} ${t('dashboard.inDiscussion')}`, pct: progressPct(metrics.signedMous, settings.tier1MousTarget) },
    { label: t('dashboard.grantPipeline'), value: fmtUsd(metrics.grantPipeline), sub: `${metrics.openGrantCount} ${t('dashboard.openApplications')}`, pct: 40 },
    { label: t('dashboard.activeVolunteers'), value: String(metrics.activeVolunteers), sub: `${metrics.volunteerHrs} ${t('dashboard.volunteerHrs')}`, pct: progressPct(metrics.volunteerHrs, 50) },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        {kpis.map((k) => (
          <div key={k.label} className="ldb-kpi-card p-5">
            <div className="ldb-sans text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">{k.label}</div>
            <div className="ldb-num text-2xl font-bold text-gray-900 leading-tight mb-1.5">{k.value}</div>
            <div className="ldb-sans text-xs text-gray-500 mb-4 min-h-[1rem]">{k.sub}</div>
            <div className="ldb-progress mt-auto"><div className="ldb-progress-bar" style={{ width: `${k.pct}%` }} /></div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="ldb-section-title text-xl font-bold mb-4">{t('dashboard.upcomingDeadlines')}</h2>
          <div className="ldb-surface p-5 space-y-3">
            {upcoming.length === 0 ? <p className="ldb-sans text-sm text-gray-500">—</p> : upcoming.map((g) => (
              <div key={g.id} className="ldb-sans flex flex-wrap items-center justify-between gap-2 text-sm border-b border-[#eeeae3] pb-3 last:border-0 last:pb-0">
                <span><strong>{g.program}</strong> — {fmtUsd(g.amount)} · {t('dashboard.due')} {g.deadline}</span>
                <Pill value={g.status} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="ldb-section-title text-xl font-bold mb-4">{t('dashboard.needsAttention')}</h2>
          <div className="ldb-surface p-5 space-y-3">
            {attention.length === 0 ? <p className="ldb-sans text-sm text-gray-500">—</p> : attention.map((a, i) => (
              <div key={i} className="ldb-sans text-sm border-b border-[#eeeae3] pb-3 last:border-0 last:pb-0">
                <div className="mb-1">{a.text}</div>
                <span className="ldb-pill border-red-600 text-red-700 bg-red-50">{a.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const ENTITY_CONFIG = {
  funders: {
    collection: 'funders', label: 'Funder',
    fields: [
      { key: 'organization', label: 'Organization' }, { key: 'type', label: 'Type' },
      { key: 'circle', label: 'Circle', type: 'select', options: [{ value: 'inner', label: 'Inner Circle' }, { value: 'middle', label: 'Middle Circle' }, { value: 'outer', label: 'Outer Circle' }] },
      { key: 'stage', label: 'Stage', type: 'select', options: [{ value: 'research', label: 'Research' }, { value: 'outreach', label: 'Outreach' }, { value: 'cultivation', label: 'Cultivation' }, { value: 'committed', label: 'Committed' }] },
      { key: 'ask', label: 'Ask (USD)', type: 'number' }, { key: 'committed', label: 'Committed (USD)', type: 'number' },
      { key: 'owner', label: 'Owner' }, { key: 'nextStep', label: 'Next step', type: 'textarea' },
    ],
    headers: ['Organization', 'Circle', 'Stage', 'Ask (USD)', 'Committed (USD)', 'Owner', 'Next step', 'Actions'],
    render: (r) => (
      <>
        <td><div className="font-bold">{r.organization}</div><div className="ldb-sans text-xs text-gray-500">{r.type}</div></td>
        <td><Pill value={r.circle} label={r.circle === 'inner' ? 'INNER CIRCLE' : r.circle === 'middle' ? 'MIDDLE CIRCLE' : 'OUTER CIRCLE'} /></td>
        <td><Pill value={r.stage} /></td>
        <td className="ldb-sans">{fmtUsd(r.ask)}</td>
        <td className={`ldb-sans ${r.committed >= r.ask && r.committed > 0 ? 'text-green-700 font-semibold' : ''}`}>{fmtUsd(r.committed)}</td>
        <td className="ldb-sans">{r.owner}</td>
        <td className="ldb-sans text-sm max-w-xs">{r.nextStep}</td>
      </>
    ),
  },
  grants: {
    collection: 'grants', label: 'Grant',
    fields: [
      { key: 'program', label: 'Funder / Program' }, { key: 'amount', label: 'Amount (USD)', type: 'number' },
      { key: 'deadline', label: 'Deadline', type: 'date' },
      { key: 'compliance', label: 'Charter compliance', type: 'select', options: [{ value: 'ceo', label: 'CEO' }, { value: 'board', label: 'Board approval + Chair co-sign' }] },
      { key: 'status', label: 'Status', type: 'select', options: [{ value: 'researching', label: 'Researching' }, { value: 'drafting', label: 'Drafting' }, { value: 'submitted', label: 'Submitted' }, { value: 'awarded', label: 'Awarded' }] },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    headers: ['Funder / Program', 'Amount (USD)', 'Deadline', 'Charter Compliance', 'Status', 'Notes', 'Actions'],
    render: (r) => (
      <>
        <td className="font-bold max-w-xs">{r.program}</td>
        <td className="ldb-sans">{fmtUsd(r.amount)}</td>
        <td className="ldb-sans">{r.deadline}</td>
        <td><Pill value={r.compliance} label={r.compliance === 'board' ? 'BOARD APPROVAL + CHAIR CO-SIGN REQUIRED' : 'CEO'} /></td>
        <td><Pill value={r.status} /></td>
        <td className="ldb-sans text-sm max-w-xs">{r.notes}</td>
      </>
    ),
  },
  partners: {
    collection: 'partners', label: 'Partner',
    fields: [
      { key: 'organization', label: 'Organization' },
      { key: 'tier', label: 'Tier', type: 'select', options: [{ value: 'tier1', label: 'Tier 1' }] },
      { key: 'status', label: 'Status', type: 'select', options: [{ value: 'target', label: 'Target' }, { value: 'in_discussion', label: 'In Discussion' }, { value: 'signed', label: 'Signed' }] },
      { key: 'signedDate', label: 'Signed date', type: 'date' }, { key: 'expiryDate', label: 'Expiry date', type: 'date' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    headers: ['Organization', 'Tier', 'Status', 'Signed date', 'Expiry date', 'Notes', 'Actions'],
    render: (r) => (
      <>
        <td><div className="font-bold">{r.organization}</div></td>
        <td><Pill value={r.tier} label="TIER 1" /></td>
        <td><Pill value={r.status} label={r.status === 'in_discussion' ? 'IN DISCUSSION' : r.status.toUpperCase()} /></td>
        <td className="ldb-sans">{r.signedDate || '—'}</td>
        <td className="ldb-sans">{r.expiryDate || '—'}</td>
        <td className="ldb-sans text-sm max-w-xs">{r.notes}</td>
      </>
    ),
  },
  subscribers: {
    collection: 'subscribers', label: 'Subscriber',
    fields: [
      { key: 'name', label: 'Name' },
      { key: 'plan', label: 'Plan', type: 'select', options: [{ value: 'individual', label: 'Individual' }, { value: 'family', label: 'Family' }] },
      { key: 'fee', label: 'Monthly fee (USD)', type: 'number' },
      { key: 'startDate', label: 'Start date', type: 'date' },
      { key: 'status', label: 'Status', type: 'select', options: [{ value: 'trial', label: 'Trial' }, { value: 'active', label: 'Active' }, { value: 'churned', label: 'Churned' }] },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    headers: ['Name', 'Plan', 'Monthly fee (USD)', 'Start date', 'Status', 'Notes', 'Actions'],
    render: (r) => (
      <>
        <td className="font-bold">{r.name}</td>
        <td className="ldb-sans capitalize">{r.plan}</td>
        <td className="ldb-sans">${r.fee}/mo</td>
        <td className="ldb-sans">{r.startDate}</td>
        <td><Pill value={r.status} /></td>
        <td className="ldb-sans text-sm max-w-xs">{r.notes}</td>
      </>
    ),
  },
  volunteers: {
    collection: 'volunteers', label: 'Volunteer',
    fields: [
      { key: 'name', label: 'Name' }, { key: 'role', label: 'Role / title' },
      { key: 'area', label: 'Function area', type: 'select', options: [{ value: 'leadership', label: 'Leadership' }, { value: 'engineering', label: 'Engineering' }, { value: 'operations', label: 'Operations' }, { value: 'security', label: 'Security' }, { value: 'grc', label: 'GRC' }, { value: 'legal', label: 'Legal' }] },
      { key: 'hours', label: 'Hours / week', type: 'number' },
      { key: 'status', label: 'Status', type: 'select', options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    headers: ['Name', 'Function area', 'Hours / week', 'Status', 'Notes', 'Actions'],
    render: (r) => (
      <>
        <td><div className="font-bold">{r.name}</div><div className="ldb-sans text-xs text-gray-500">{r.role}</div></td>
        <td><Pill value={r.area} label={r.area.toUpperCase()} /></td>
        <td className="ldb-sans">{r.hours}</td>
        <td><Pill value={r.status} /></td>
        <td className="ldb-sans text-sm max-w-xs">{r.notes}</td>
      </>
    ),
  },
  followups: {
    collection: 'followups', label: 'Follow-up',
    fields: [
      { key: 'organization', label: 'Organization' },
      { key: 'type', label: 'Type', type: 'select', options: [{ value: 'call', label: 'Call' }, { value: 'meeting', label: 'Meeting' }, { value: 'email', label: 'Email' }] },
      { key: 'dueDate', label: 'Due date', type: 'date' },
      { key: 'status', label: 'Status', type: 'select', options: [{ value: 'pending', label: 'Pending' }, { value: 'overdue', label: 'Overdue' }, { value: 'done', label: 'Done' }] },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    headers: ['Organization', 'Type', 'Due date', 'Status', 'Notes', 'Actions'],
    render: (r) => (
      <>
        <td className="font-bold">{r.organization}</td>
        <td><Pill value={r.type} /></td>
        <td className={`ldb-sans ${r.status === 'overdue' || isOverdue(r.dueDate) ? 'text-red-600 font-semibold' : ''}`}>{r.dueDate}</td>
        <td><Pill value={r.status} /></td>
        <td className="ldb-sans text-sm max-w-xs">{r.notes}</td>
      </>
    ),
  },
};

function CrudView({ section }) {
  const { state, lang, upsertItem, deleteItem } = useLdb();
  const cfg = ENTITY_CONFIG[section];
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const t = (k) => ldbT(lang, k);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const list = state[cfg.collection] || [];
    if (!q) return list;
    return list.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [state, cfg.collection, search]);

  const handleSave = (form) => {
    upsertItem(cfg.collection, form, cfg.label);
    setModal(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-3xl font-bold">{t(`titles.${section}`)}</h1>
        <div className="flex flex-wrap items-center gap-2 ldb-sans">
          <input className="ldb-input px-3 py-1.5 text-sm w-40" placeholder={t('search')} value={search} onChange={(e) => setSearch(e.target.value)} />
          <button type="button" className="ldb-btn-outline-dark px-3 py-1.5 text-sm" onClick={() => downloadCsv(`${section}.csv`, cfg.fields.map((f) => f.label), filtered.map((r) => cfg.fields.map((f) => r[f.key])))}>{t('csv')}</button>
          <button type="button" className="ldb-btn-primary px-4 py-1.5 text-sm" onClick={() => setModal({})}>+ {t(`addLabels.${section}`)}</button>
        </div>
      </div>
      <div className="ldb-table-wrap overflow-x-auto">
        <table className="ldb-table w-full text-left">
          <thead><tr>{cfg.headers.map((h) => <th key={h} className="ldb-sans px-4 py-3">{h}</th>)}</tr></thead>
          <tbody className="ldb-sans text-sm">
            {filtered.map((r) => (
              <tr key={r.id}>
                {cfg.render(r)}
                <td className="px-4 py-3 whitespace-nowrap">
                  <button type="button" className="ldb-btn-outline-dark ldb-btn-sm mr-1" onClick={() => setModal(r)}>{t('edit')}</button>
                  <button type="button" className="ldb-btn-outline-dark ldb-btn-sm" onClick={() => { if (window.confirm('Delete?')) deleteItem(cfg.collection, r.id, cfg.label, r.organization || r.name); }}>{t('del')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal !== null && <LdbModal title={modal.id ? `Edit ${cfg.label}` : t(`addLabels.${section}`)} fields={cfg.fields} initial={modal} onSave={handleSave} onClose={() => setModal(null)} />}
    </div>
  );
}

function BoardReportView() {
  const { state, metrics, lang, logActivity } = useLdb();
  const [period, setPeriod] = useState('June 2026');
  const [report, setReport] = useState('');
  const t = (k) => ldbT(lang, k);

  const generate = () => {
    const s = state.settings;
    const text = [
      `CCAI Board Report — ${period}`, `Organization: ${s.orgNameEn}`, '',
      '## Executive summary',
      `Funds raised: ${fmtUsd(metrics.fundsRaised)} (target floor ${fmtUsd(s.raiseFloor)})`,
      `Paying subscribers: ${metrics.activeSubs} / ${s.payingSubscribersTarget}`,
      `Signed Tier-1 MOUs: ${metrics.signedMous} / ${s.tier1MousTarget}`,
      `Open grant pipeline: ${fmtUsd(metrics.grantPipeline)}`,
      `Active volunteers: ${metrics.activeVolunteers} (${metrics.volunteerHrs} hrs/week)`, '',
      '## Funder pipeline', ...state.funders.map((f) => `• ${f.organization} — ${f.stage}, ${fmtUsd(f.committed)} / ${fmtUsd(f.ask)}`), '',
      '## Grants', ...state.grants.map((g) => `• ${g.program} — ${g.status}, ${fmtUsd(g.amount)}, due ${g.deadline}`), '',
      '## Partners', ...state.partners.map((p) => `• ${p.organization} — ${p.status}`), '',
      '## Risks & follow-ups', ...state.followups.filter((f) => f.status === 'overdue').map((f) => `• OVERDUE: ${f.organization} (${f.dueDate})`), '',
      '— Generated by CCAI Leadership Dashboard (demo)',
    ].join('\n');
    setReport(text);
    logActivity('Board report generated', period);
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between gap-4 mb-6">
        <h1 className="text-3xl font-bold">{t('titles.board-report')}</h1>
        <p className="ldb-sans text-sm text-gray-500 self-end">{t('boardNote')}</p>
      </div>
      <div className="max-w-md ldb-table-wrap p-6">
        <label className="ldb-sans block text-[10px] font-bold uppercase text-gray-500 mb-1">{t('reportingPeriod')}</label>
        <input className="ldb-input ldb-sans w-full px-3 py-2 mb-4" value={period} onChange={(e) => setPeriod(e.target.value)} />
        <button type="button" className="ldb-btn-primary ldb-sans w-full py-3 text-sm" onClick={generate}>{t('generateReport')}</button>
      </div>
      {report && <pre className="ldb-report-output mt-8 p-6 text-sm max-w-3xl">{report}</pre>}
    </div>
  );
}

function ActivityLogView() {
  const { state, lang, clearActivityLog } = useLdb();
  const t = (k) => ldbT(lang, k);
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">{t('titles.activity-log')}</h1>
        <button type="button" className="ldb-btn-outline-dark ldb-sans px-3 py-1.5 text-sm" onClick={clearActivityLog}>{t('clearLog')}</button>
      </div>
      <div className="ldb-table-wrap overflow-x-auto">
        <table className="ldb-table w-full">
          <thead><tr><th className="ldb-sans px-4 py-3">When</th><th className="ldb-sans px-4 py-3">Action</th><th className="ldb-sans px-4 py-3">Detail</th></tr></thead>
          <tbody className="ldb-sans text-sm">
            {state.activityLog.length === 0 ? (
              <tr><td colSpan={3} className="text-center text-gray-400" style={{ padding: '3rem 1rem' }}>{t('noActivity')}</td></tr>
            ) : state.activityLog.map((e) => (
              <tr key={e.id}>
                <td className="px-4 py-3 whitespace-nowrap">{new Date(e.when).toLocaleString()}</td>
                <td className="px-4 py-3">{e.action}</td>
                <td className="px-4 py-3">{e.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsView() {
  const { state, lang, updateSettings } = useLdb();
  const [form, setForm] = useState({ ...state.settings });
  const t = (k) => ldbT(lang, k);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="flex justify-between mb-6">
        <h1 className="text-3xl font-bold">{t('titles.settings')}</h1>
        <p className="ldb-sans text-sm text-gray-500">{t('settingsNote')}</p>
      </div>
      <div className="max-w-xl ldb-table-wrap p-6 space-y-4">
        {[
          { key: 'orgNameEn', label: t('orgNameEn') }, { key: 'orgNameZh', label: t('orgNameZh') }, { key: 'website', label: t('website') },
          { key: 'raiseFloor', label: t('raiseFloor'), type: 'number' }, { key: 'raiseStretch', label: t('raiseStretch'), type: 'number' },
          { key: 'payingSubscribersTarget', label: t('payingSubscribersTarget'), type: 'number' }, { key: 'tier1MousTarget', label: t('tier1MousTarget'), type: 'number' },
        ].map((f) => (
          <label key={f.key} className="ldb-sans block text-[10px] font-bold uppercase text-gray-500">
            {f.label}
            <input type={f.type || 'text'} className="ldb-input mt-1 w-full px-3 py-2 text-sm block" value={form[f.key]} onChange={(e) => set(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)} />
          </label>
        ))}
        <button type="button" className="ldb-btn-primary ldb-sans w-full py-3 text-sm mt-4" onClick={() => updateSettings(form)}>{t('saveSettings')}</button>
      </div>
    </div>
  );
}

function LdbShell() {
  const { lang, setLang, metrics, exportData, importData, loading, error } = useLdb();
  const { section = 'dashboard' } = useParams();
  const fileRef = useRef();
  const active = section;
  const t = (k) => ldbT(lang, k);

  const content = {
    dashboard: <DashboardView />,
    funders: <CrudView section="funders" />,
    grants: <CrudView section="grants" />,
    partners: <CrudView section="partners" />,
    subscribers: <CrudView section="subscribers" />,
    volunteers: <CrudView section="volunteers" />,
    followups: <CrudView section="followups" />,
    'board-report': <BoardReportView />,
    'activity-log': <ActivityLogView />,
    settings: <SettingsView />,
  }[active] || <Navigate to={LDB_BASE} replace />;

  return (
    <div className="ldb-root">
      <div className="ldb-demo-banner ldb-sans text-center py-2 px-4">{t('demoBanner')}</div>
      <header className="ldb-header">
        <div className="flex flex-wrap items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 py-3.5 border-b border-white/10">
          <div className="flex items-baseline gap-2">
            <Link to={LDB_BASE} className="text-xl font-bold text-white no-underline">
              <span className="ldb-sans font-semibold">{t('brandShort')}</span>{' '}
              <span>{t('brand')}</span>
            </Link>
            <span className="ldb-sans text-xs text-white/50 hidden sm:inline">{t('subtitle')}</span>
          </div>
          <div className="flex items-center gap-2 ldb-sans text-sm">
            <button type="button" className={`px-2.5 py-1 rounded-md transition-colors ${lang === 'en' ? 'bg-[var(--ldb-red)] text-white' : 'text-white/70 hover:text-white'}`} onClick={() => setLang('en')}>EN</button>
            <button type="button" className={`px-2.5 py-1 rounded-md transition-colors ${lang === 'zh' ? 'bg-[var(--ldb-red)] text-white' : 'text-white/70 hover:text-white'}`} onClick={() => setLang('zh')}>中文</button>
            <button type="button" className="ldb-btn-outline ldb-sans px-3 py-1 text-xs ml-2" onClick={() => {
              const blob = new Blob([JSON.stringify(exportData(), null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `ccai-leadership-dashboard-export.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}>{t('export')}</button>
            <button type="button" className="ldb-btn-outline ldb-sans px-3 py-1 text-xs" onClick={() => fileRef.current?.click()}>{t('import')}</button>
            <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => { try { importData(reader.result); } catch { alert('Invalid JSON'); } };
              reader.readAsText(file);
              e.target.value = '';
            }} />
          </div>
        </div>
        <nav className="flex overflow-x-auto px-2 sm:px-4 lg:px-6 ldb-sans text-xs sm:text-sm">
          {LDB_SECTIONS.map((s) => {
            const badge = s.badgeKey ? metrics.counts[s.badgeKey] : s.badge;
            const alert = s.alertBadgeKey ? metrics[s.alertBadgeKey] : s.alertBadge;
            return (
              <Link key={s.id} to={s.id === 'dashboard' ? LDB_BASE : `${LDB_BASE}/${s.id}`} className={`ldb-nav-link whitespace-nowrap px-3 py-3 flex items-center no-underline ${active === s.id ? 'active' : ''}`}>
                {t(`nav.${s.id}`)}
                {badge != null && badge > 0 && <span className="ldb-badge">{badge}</span>}
                {alert != null && alert > 0 && <AlertTriangle className="h-3 w-3 text-red-400 ml-1" />}
              </Link>
            );
          })}
        </nav>
      </header>
      <main className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto">
        {error && (
          <div className="ldb-sans mb-6 border border-red-300 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-[var(--ldb-red)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          content
        )}
      </main>
    </div>
  );
}

export default function Ldb() {
  return (
    <LdbProvider>
      <Routes>
        <Route index element={<LdbShell />} />
        <Route path=":section" element={<LdbShell />} />
      </Routes>
    </LdbProvider>
  );
}
